import * as THREE from "three";
import {
  Symbol3DMetadata,
  SymbolType,
  Wall3DMetadata,
  WallType,
} from "../../../types/floorplannerTypes";
import {
  CatalogSurfaceReference,
  FloorPlan3DMaterialSlot,
  Product3DMaterialAssignment,
} from "../products/product3DTypes";
import { getBuiltInFurnitureMaterial } from "./builtInFurnitureMaterials";
export type { FloorPlan3DMaterialSlot } from "../products/product3DTypes";

type MaterialOwner = {
  fillColor?: number;
  threeD?: Wall3DMetadata | Symbol3DMetadata;
};

export type FloorPlan3DMaterialSelectionState = {
  defaultColor?: THREE.ColorRepresentation;
  materialId?: string;
};

export type FloorPlan3DMaterialResolution = {
  color: THREE.Color;
  materialId?: string;
  roughness?: number;
  metalness?: number;
  transmission?: number;
  ior?: number;
  thickness?: number;
};

const DEFAULT_SLOT_COLORS: Record<FloorPlan3DMaterialSlot, number> = {
  floor: 0xe9e1d2,
  wall: 0xd8d0c4,
  joint: 0xd8d0c4,
  stair: 0xb8ad9f,
  windowGlass: 0x8cc9e8,
  doorPanel: 0xffffff,
  doorFrame: 0xf2f1ed,
  doorHardware: 0xc4c8cc,
  doorHandle: 0xc9cdd1,
  furnitureFabric: 0xc9b8a4,
  furnitureWood: 0xb98f62,
  furnitureMetal: 0xb7bbc0,
  furnitureScreen: 0x15171a,
  furnitureGlass: 0xd6e3e8,
  botanicalFoliage: 0x3f8f4a,
  botanicalFoliageDeep: 0x2a6634,
  botanicalBranch: 0x8a5b32,
  botanicalPot: 0x9a6845,
};

const LEGACY_MATERIAL_CATALOG: Record<string, THREE.ColorRepresentation> = {
  "wall-plaster": 0xd8d0c4,
  "floor-warm-neutral": 0xe9e1d2,
  "screen-dark": 0x15171a,
  "foliage-green": 0x3f8f4a,
  "branch-brown": 0x8a5b32,
};

export function resolveMaterial(
  slot: FloorPlan3DMaterialSlot,
  owner?: MaterialOwner | WallType | SymbolType | null,
  selectionState: FloorPlan3DMaterialSelectionState = {},
): FloorPlan3DMaterialResolution {
  const assignment = getSlotAssignment(slot, owner?.threeD);
  const explicitMaterialId =
    selectionState.materialId ??
    assignment?.materialId ??
    getOwnerMaterialId(owner?.threeD);
  const surfaceRef =
    assignment?.surfaceRef ?? getOwnerSurfaceRef(owner?.threeD);

  const builtIn = getBuiltInFurnitureMaterial(
    explicitMaterialId ?? surfaceRef?.id,
  );
  if (builtIn) {
    return {
      color: new THREE.Color(surfaceRef?.color ?? builtIn.color),
      materialId: builtIn.id,
      roughness: surfaceRef?.roughness ?? builtIn.roughness,
      metalness: surfaceRef?.metalness ?? builtIn.metalness,
      transmission: builtIn.transmission,
      ior: builtIn.ior,
      thickness: builtIn.thickness,
    };
  }

  const legacyColor = explicitMaterialId
    ? LEGACY_MATERIAL_CATALOG[explicitMaterialId]
    : undefined;

  if (legacyColor !== undefined) {
    return {
      color: new THREE.Color(legacyColor),
      materialId: explicitMaterialId,
    };
  }

  if (surfaceRef?.color !== undefined) {
    return {
      color: new THREE.Color(surfaceRef.color),
      materialId: explicitMaterialId,
      roughness: surfaceRef.roughness,
      metalness: surfaceRef.metalness,
    };
  }

  // Catalog UUID assignments are not in the local catalogs; keep slot default
  // instead of falling through to fill color.
  if (explicitMaterialId || surfaceRef?.id) {
    return {
      color: new THREE.Color(
        selectionState.defaultColor ?? DEFAULT_SLOT_COLORS[slot],
      ),
      materialId: explicitMaterialId,
      roughness: surfaceRef?.roughness,
      metalness: surfaceRef?.metalness,
    };
  }

  if (owner?.fillColor !== undefined) {
    return { color: new THREE.Color(owner.fillColor) };
  }

  return {
    color: new THREE.Color(
      selectionState.defaultColor ?? DEFAULT_SLOT_COLORS[slot],
    ),
  };
}

function getOwnerMaterialId(
  metadata: Wall3DMetadata | Symbol3DMetadata | undefined,
): string | undefined {
  return metadata && "materialId" in metadata ? metadata.materialId : undefined;
}

function getOwnerSurfaceRef(
  metadata: Wall3DMetadata | Symbol3DMetadata | undefined,
): CatalogSurfaceReference | undefined {
  return metadata && "surfaceRef" in metadata
    ? metadata.surfaceRef
    : undefined;
}

function getSlotAssignment(
  slot: FloorPlan3DMaterialSlot,
  metadata: Wall3DMetadata | Symbol3DMetadata | undefined,
): Product3DMaterialAssignment | undefined {
  if (!metadata || !("materialAssignments" in metadata)) return undefined;
  return metadata.materialAssignments?.[slot];
}
